import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { useSettingsStore } from '../stores/useSettingsStore';
import TokenModal from '../components/TokenModal';
import { PageHeader, Button, EmptyState, LoadingSpinner, Checkbox, Modal } from '../components/ui';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { authStatus, setAuthStatus } = useAuth();
  const { showToast } = useToast();

  // Use Zustand stores
  const subscription = useSettingsStore(state => state.subscription);
  const apiTokens = useSettingsStore(state => state.apiTokens);
  const loadingTokens = useSettingsStore(state => state.loadingTokens);
  const hasLoadedOnce = useSettingsStore(state => state.hasLoadedOnce);
  const setCurrentUsername = useSettingsStore(state => state.setCurrentUsername);
  const loadSubscriptionData = useSettingsStore(state => state.loadSubscriptionData);
  const loadApiTokens = useSettingsStore(state => state.loadApiTokens);
  const revokeApiTokenFromStore = useSettingsStore(state => state.revokeApiToken);
  const createApiTokenInStore = useSettingsStore(state => state.createApiToken);

  const [showTokenModal, setShowTokenModal] = useState(false);
  const [showCreateTokenModal, setShowCreateTokenModal] = useState(false);
  const [newTokenData, setNewTokenData] = useState<{ token: string; name: string } | null>(null);

  // Webhook state
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [loadingWebhooks, setLoadingWebhooks] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [newWebhookSecret, setNewWebhookSecret] = useState<string | null>(null);

  const [orgSettings, setOrgSettings] = useState({
    enableComments: true,
  });

  const [localSettings, setLocalSettings] = useState({
    enableToasts: true,
  });

  // Set username in store and load data only once
  useEffect(() => {
    if (authStatus.user?.login) {
      setCurrentUsername(authStatus.user.login);
    }
  }, [authStatus.user?.login, setCurrentUsername]);

  useEffect(() => {
    if (!hasLoadedOnce && authStatus.user?.login && authStatus.currentOrganization) {
      loadSubscriptionData();
      loadApiTokens();
      loadWebhooks();
    }
  }, [hasLoadedOnce, authStatus.user?.login, authStatus.currentOrganization, loadSubscriptionData]);

  useEffect(() => {
    loadLocalSettings();
  }, []);

  useEffect(() => {
    if (authStatus.currentOrganization) {
      setOrgSettings({
        enableComments: authStatus.currentOrganization.enableComments ?? true,
      });
    }
  }, [authStatus.currentOrganization]);

  function loadLocalSettings() {
    const saved = localStorage.getItem('yukonSettings');
    if (saved) {
      setLocalSettings(JSON.parse(saved));
    }
  }

  async function saveOrgPreferences() {
    try {
      const response = await fetch('/api/organizations/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(orgSettings),
      });
      const data = await response.json();

      if (data.success) {
        setAuthStatus(prev => ({
          ...prev,
          currentOrganization: data.organization,
        }));
        showToast('Organization preferences saved', 'success');
      }
    } catch (error) {
      showToast('Failed to save organization preferences', 'error');
    }
  }

  function saveLocalSettings() {
    localStorage.setItem('yukonSettings', JSON.stringify(localSettings));
    showToast('Settings saved successfully', 'success');
  }

  async function createApiToken(tokenData: { name: string; expiresIn: string; scopes: string[] }) {
    const result = await createApiTokenInStore(tokenData);

    if (result.success) {
      setNewTokenData({ token: result.token!, name: tokenData.name });
      setShowCreateTokenModal(false);
      setShowTokenModal(true);
      showToast('API token created successfully', 'success');
    } else {
      showToast(result.error || 'Failed to create token', 'error');
    }
  }

  async function revokeApiToken(tokenId: string, tokenName: string) {
    if (!confirm(`Revoke token "${tokenName}"? This cannot be undone.`)) {
      return;
    }

    const success = await revokeApiTokenFromStore(tokenId);

    if (success) {
      showToast('Token revoked', 'success');
    } else {
      showToast('Failed to revoke token', 'error');
    }
  }

  function copyToken(token: string) {
    navigator.clipboard.writeText(token);
    showToast('Token copied to clipboard', 'success');
  }

  // Webhook functions
  async function loadWebhooks() {
    setLoadingWebhooks(true);
    try {
      const response = await fetch('/api/v1/webhooks', {
        credentials: 'same-origin',
      });
      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks || []);
      }
    } catch (error) {
      console.error('Failed to load webhooks:', error);
    } finally {
      setLoadingWebhooks(false);
    }
  }

  async function createWebhook() {
    // Check if Pro tier
    if (subscription?.tier !== 'PRO') {
      if (confirm('Webhooks are only available on the Pro plan. Would you like to upgrade?')) {
        handleUpgrade();
      }
      return;
    }

    const url = prompt('Enter webhook URL (e.g., https://example.com/webhook):');
    if (!url) return;

    try {
      const response = await fetch('/api/v1/webhooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          url,
          events: ['repository.frozen', 'repository.unfrozen'],
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setNewWebhookSecret(data.webhook.secret);
        setShowWebhookModal(true);
        await loadWebhooks();
        showToast('Webhook created successfully', 'success');
      } else {
        showToast(data.error || 'Failed to create webhook', 'error');
      }
    } catch (error) {
      showToast('Failed to create webhook', 'error');
    }
  }

  async function deleteWebhook(webhookId: string, url: string) {
    if (!confirm(`Delete webhook ${url}?`)) {
      return;
    }

    try {
      const response = await fetch(`/api/v1/webhooks/${webhookId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });

      if (response.ok) {
        await loadWebhooks();
        showToast('Webhook deleted', 'success');
      } else {
        showToast('Failed to delete webhook', 'error');
      }
    } catch (error) {
      showToast('Failed to delete webhook', 'error');
    }
  }

  async function testWebhook(webhookId: string) {
    try {
      const response = await fetch(`/api/v1/webhooks/${webhookId}/test`, {
        method: 'POST',
        credentials: 'same-origin',
      });

      const data = await response.json();
      if (response.ok && data.success) {
        showToast('Test webhook sent successfully', 'success');
      } else {
        showToast(data.error || 'Failed to send test webhook', 'error');
      }
    } catch (error) {
      showToast('Failed to send test webhook', 'error');
    }
  }

  async function toggleWebhook(webhookId: string, enabled: boolean) {
    try {
      const response = await fetch(`/api/v1/webhooks/${webhookId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ enabled: !enabled }),
      });

      if (response.ok) {
        await loadWebhooks();
        showToast(`Webhook ${!enabled ? 'enabled' : 'disabled'}`, 'success');
      } else {
        showToast('Failed to update webhook', 'error');
      }
    } catch (error) {
      showToast('Failed to update webhook', 'error');
    }
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  async function handleUpgrade() {
    try {
      const response = await fetch('/api/organizations/subscription/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ billingInterval: 'monthly' }),
      });
      const data = await response.json();
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      }
    } catch (error) {
      showToast('Failed to start checkout', 'error');
    }
  }

  return (
    <div className="page">
      <PageHeader title="Settings" subtitle="Configure Yukon preferences" />

      <div className="page-content">
        {/* GitHub App Installation - Priority when no organization */}
        {!authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>Get Started</h3>
            <p className="section-description">Install the GitHub App to start managing freezes</p>
            <div className="empty-state" style={{ marginTop: '20px' }}>
              <div className="empty-icon">
                <i className="fa-solid fa-download"></i>
              </div>
              <h3>GitHub App Not Installed</h3>
              <p>Install the GitHub App on your repositories to start using Yukon</p>
              <div className="empty-state-actions">
                <a
                  href={`https://github.com/apps/${import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager'}/installations/new`}
                  className="btn btn-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fa-brands fa-github"></i>
                  <span>Configure GitHub App</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* GitHub App Installation - Only show if organization exists */}
        {authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>GitHub App</h3>
            <p className="section-description">Manage which repositories Yukon can access</p>
            <div
              style={{
                padding: '12px 16px',
                background: '#f6f8fa',
                border: '1px solid #d0d7de',
                borderRadius: '6px',
                marginBottom: '16px',
                fontSize: '14px',
                color: '#57606a',
              }}
            >
              <i className="fa-solid fa-info-circle" style={{ marginRight: '8px' }}></i>
              <strong>Note:</strong> Private repositories require the owner to have GitHub Pro,
              Team, or Enterprise to enable branch protection.{' '}
              <a
                href="https://docs.github.com/en/get-started/learning-about-github/githubs-plans"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#0969da', textDecoration: 'none' }}
              >
                Learn more →
              </a>
            </div>
            <div className="settings-actions">
              <a
                href={`https://github.com/apps/${import.meta.env.VITE_PUBLIC_GITHUB_APP_SLUG || 'yukon-freeze-manager'}/installations/new`}
                className="btn btn-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fa-brands fa-github"></i>
                <span>Configure Repositories</span>
              </a>
            </div>
          </div>
        )}

        {/* Organization Settings - Only show if organization exists */}
        {authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>Organization Settings</h3>
            <p className="section-description">
              Settings that apply to all members of this organization
            </p>

            <div className="settings-group">
              <h4>GitHub Comments</h4>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={orgSettings.enableComments}
                  onChange={e =>
                    setOrgSettings({ ...orgSettings, enableComments: e.target.checked })
                  }
                />
                <span className="checkbox-custom"></span>
                <div>
                  <div className="checkbox-title">
                    Add freeze/unfreeze comments to pull requests
                  </div>
                  <div className="checkbox-description">
                    Old comments are always removed to keep PRs clean
                  </div>
                </div>
              </label>
            </div>

            <div className="settings-actions">
              <button onClick={saveOrgPreferences} className="btn btn-primary">
                Save Organization Settings
              </button>
            </div>
          </div>
        )}

        {/* API Tokens - Only show if organization exists */}
        {authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>Your API Tokens</h3>
            <p className="section-description">
              Manage your personal API tokens for programmatic access via CLI, MCP, and other
              integrations
            </p>

            <div className="tokens-list">
              {loadingTokens ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                </div>
              ) : apiTokens.length === 0 ? (
                <EmptyState
                  icon="fa-solid fa-key"
                  title={
                    subscription?.tier === 'PRO'
                      ? 'No API tokens yet. Create one to get started with integrations.'
                      : 'Upgrade to Pro to create API tokens.'
                  }
                />
              ) : (
                apiTokens.map(token => (
                  <div key={token.id} className="token-item">
                    <div className="token-info">
                      <div className="token-name">{token.name}</div>
                      <div className="token-meta">
                        <span className="token-prefix">{token.tokenPrefix}...</span>
                        <span className="token-detail">Created {formatDate(token.createdAt)}</span>
                        {token.lastUsedAt && (
                          <span className="token-detail">
                            Last used {formatDate(token.lastUsedAt)}
                          </span>
                        )}
                        <span className="token-detail">Expires {formatDate(token.expiresAt)}</span>
                      </div>
                    </div>
                    <button
                      className="btn-icon delete-btn"
                      onClick={() => revokeApiToken(token.id, token.name)}
                      title="Revoke token"
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="settings-actions">
              <button
                onClick={() =>
                  subscription?.tier === 'PRO' ? setShowCreateTokenModal(true) : handleUpgrade()
                }
                className="btn btn-primary"
              >
                <i
                  className={
                    subscription?.tier === 'PRO' ? 'fa-solid fa-plus' : 'fa-solid fa-crown'
                  }
                ></i>
                <span>{subscription?.tier === 'PRO' ? 'Create API Token' : 'Upgrade to Pro'}</span>
              </button>
              <button onClick={() => navigate('/api-docs')} className="btn btn-secondary">
                <i className="fa-solid fa-book"></i>
                <span>View API Docs</span>
              </button>
            </div>
          </div>
        )}

        {/* Webhooks Section - Only show if organization exists */}
        {authStatus.currentInstallation && (
          <div className="settings-section">
            <h3>Webhooks</h3>
            <p className="section-description">
              Configure webhooks to receive freeze/unfreeze events at your custom endpoints
            </p>

            <div className="tokens-list">
              {loadingWebhooks ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                </div>
              ) : webhooks.length === 0 ? (
                <EmptyState
                  icon="fa-solid fa-arrow-right-arrow-left"
                  title={
                    subscription?.tier === 'PRO'
                      ? 'No webhooks configured yet. Create one to receive events.'
                      : 'Upgrade to Pro to create webhooks.'
                  }
                />
              ) : (
                webhooks.map(webhook => (
                  <div key={webhook.id} className="token-item">
                    <div className="token-info">
                      <div className="token-name">
                        {webhook.url}
                        {!webhook.enabled && (
                          <span
                            style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              background: '#666',
                              borderRadius: '4px',
                              fontSize: '12px',
                            }}
                          >
                            Disabled
                          </span>
                        )}
                      </div>
                      <div className="token-meta">
                        <span className="token-detail">Events: {webhook.events.join(', ')}</span>
                        <span className="token-detail">
                          Created {formatDate(webhook.createdAt)}
                        </span>
                        <span className="token-detail">by {webhook.createdBy}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn-icon"
                        onClick={() => testWebhook(webhook.id)}
                        title="Send test event"
                        style={{ background: '#4a90e2', color: 'white' }}
                      >
                        <i className="fa-solid fa-paper-plane"></i>
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => toggleWebhook(webhook.id, webhook.enabled)}
                        title={webhook.enabled ? 'Disable' : 'Enable'}
                        style={{
                          background: webhook.enabled ? '#f39c12' : '#27ae60',
                          color: 'white',
                        }}
                      >
                        <i className={`fa-solid ${webhook.enabled ? 'fa-pause' : 'fa-play'}`}></i>
                      </button>
                      <button
                        className="btn-icon delete-btn"
                        onClick={() => deleteWebhook(webhook.id, webhook.url)}
                        title="Delete webhook"
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="settings-actions">
              <button
                onClick={subscription?.tier === 'PRO' ? createWebhook : handleUpgrade}
                className="btn btn-primary"
              >
                <i
                  className={
                    subscription?.tier === 'PRO' ? 'fa-solid fa-plus' : 'fa-solid fa-crown'
                  }
                ></i>
                <span>{subscription?.tier === 'PRO' ? 'Add Webhook' : 'Upgrade to Pro'}</span>
              </button>
              <button onClick={() => navigate('/api-docs')} className="btn btn-secondary">
                <i className="fa-solid fa-book"></i>
                <span>Webhook Documentation</span>
              </button>
            </div>
          </div>
        )}

        {/* Webhook Secret Display Modal */}
        {showWebhookModal && newWebhookSecret && (
          <div
            className="modal"
            style={{ display: 'block' }}
            onClick={e =>
              e.target === e.currentTarget &&
              setShowWebhookModal(false) &&
              setNewWebhookSecret(null)
            }
          >
            <div className="modal-content">
              <div className="modal-header">
                <h3>Webhook Created</h3>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowWebhookModal(false);
                    setNewWebhookSecret(null);
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="token-warning">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <p>Save this webhook secret now. You'll need it to verify webhook signatures!</p>
                </div>
                <div className="token-display">
                  <code>{newWebhookSecret}</code>
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      navigator.clipboard.writeText(newWebhookSecret);
                      showToast('Secret copied to clipboard', 'success');
                    }}
                  >
                    <i className="fa-solid fa-copy"></i>
                    <span>Copy</span>
                  </button>
                </div>
                <div className="token-usage">
                  <h4>Webhook Details:</h4>
                  <p>
                    Webhook events will be sent as HTTP POST requests with a{' '}
                    <code>X-Yukon-Signature</code> header containing an HMAC-SHA256 signature.
                  </p>
                  <p style={{ marginTop: '8px' }}>
                    <strong>Signature verification (Node.js example):</strong>
                  </p>
                  <pre style={{ fontSize: '12px' }}>
                    {`const crypto = require('crypto');

const signature = request.headers['x-yukon-signature'];
const payload = JSON.stringify(request.body);
const hash = 'sha256=' + crypto
  .createHmac('sha256', '${newWebhookSecret}')
  .update(payload)
  .digest('hex');

if (signature === hash) {
  // Signature is valid
}`}
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowWebhookModal(false);
                    setNewWebhookSecret(null);
                  }}
                >
                  I've Saved the Secret
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Token Creation Modal */}
        <TokenModal
          isOpen={showCreateTokenModal}
          onClose={() => setShowCreateTokenModal(false)}
          onSave={createApiToken}
        />

        {/* Token Display Modal */}
        {showTokenModal && newTokenData && (
          <div
            className="modal"
            style={{ display: 'block' }}
            onClick={e =>
              e.target === e.currentTarget && setShowTokenModal(false) && setNewTokenData(null)
            }
          >
            <div className="modal-content">
              <div className="modal-header">
                <h3>API Token Created</h3>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowTokenModal(false);
                    setNewTokenData(null);
                  }}
                >
                  &times;
                </button>
              </div>
              <div className="modal-body">
                <div className="token-warning">
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  <p>Save this token now. You won't be able to see it again!</p>
                </div>
                <div className="token-display">
                  <code>{newTokenData.token}</code>
                  <button
                    className="btn btn-secondary"
                    onClick={() => copyToken(newTokenData.token)}
                  >
                    <i className="fa-solid fa-copy"></i>
                    <span>Copy</span>
                  </button>
                </div>
                <div className="token-usage">
                  <h4>Usage Example:</h4>
                  <pre>
                    {`curl -H "Authorization: Bearer ${newTokenData.token}" \\
  ${window.location.origin}/api/v1/repos`}
                  </pre>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowTokenModal(false);
                    setNewTokenData(null);
                  }}
                >
                  I've Saved My Token
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Local Preferences */}
        <div className="settings-section">
          <h3>Local Preferences</h3>
          <p className="section-description">Personal settings stored in your browser</p>

          <div className="settings-group">
            <h4>Notifications</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={localSettings.enableToasts}
                onChange={e =>
                  setLocalSettings({ ...localSettings, enableToasts: e.target.checked })
                }
              />
              <span className="checkbox-custom"></span>
              <div>
                <div className="checkbox-title">Show toast notifications</div>
                <div className="checkbox-description">Display notifications for actions</div>
              </div>
            </label>
          </div>

          <div className="settings-actions">
            <button onClick={saveLocalSettings} className="btn btn-primary">
              Save Local Preferences
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
